/**
 * Team chat API client (internal Discord-like messaging).
 *
 * The server scopes everything to the caller's memberships — a conversation
 * the caller doesn't belong to simply 404s. All list reads are defensive
 * (`?? []`) so Test Mode's generic mock fallback can never blank the page.
 */
import { apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';
import { readFileAsBase64, type UploadProgressHandler } from './fileUpload';

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function jsonHeaders(): Record<string, string> {
  return { 'Content-Type': 'application/json', ...authHeaders() };
}

export interface ChatMemberInfo {
  id: string;
  name: string;
  avatarUrl: string | null;
  isActive?: boolean;
}

export interface TeamConversation {
  id: string;
  type: 'DM' | 'GROUP';
  /** Group name; null for DMs (render the peer's name from members). */
  name: string | null;
  members: ChatMemberInfo[];
  createdById: string | null;
  lastMessageAt: string | null;
  unreadCount: number;
  lastMessagePreview: { authorName: string; snippet: string } | null;
  /** The caller's own sidebar position; null until they've dragged this row. */
  sortOrder: number | null;
  createdAt: string;
}

export interface TeamChatAttachment {
  key: string;
  fileName: string;
  contentType: string;
  size: number;
  /** Signed 1h proxy link, minted per response. */
  downloadUrl?: string | null;
}

export interface TeamChatEmbed {
  url: string;
  title: string | null;
  description: string | null;
  imageUrl: string | null;
  siteName: string | null;
}

export interface TeamChatReaction {
  emoji: string;
  count: number;
  /** Whether the current viewer reacted with this emoji. */
  mine: boolean;
  userNames: string[];
}

export interface TeamChatMessage {
  id: string;
  conversationId: string;
  author: ChatMemberInfo;
  body: string;
  attachments: TeamChatAttachment[];
  embeds: TeamChatEmbed[];
  replyTo: { id: string; authorName: string; snippet: string; deleted: boolean } | null;
  reactions: TeamChatReaction[];
  pinnedAt: string | null;
  editedAt: string | null;
  deleted: boolean;
  createdAt: string;
}

export interface MessagesPage {
  /** Newest first — the UI reverses pages into chronological order. */
  messages: TeamChatMessage[];
  nextCursor: string | null;
}

export interface UnreadSummary {
  total: number;
  byConversation: Record<string, number>;
}

/**
 * One member's read watermark — the raw material for the "seen" ticks.
 * `lastReadAt >= message.createdAt` means that member has seen the message,
 * which is exactly the complement of the unread count, so the badge and the
 * ticks can never disagree.
 */
export interface ChatReadReceipt {
  userId: string;
  name: string;
  /** Deactivated accounts stay in the conversation but will never read again. */
  isActive: boolean;
  joinedAt: string;
  lastReadAt: string | null;
}

export async function listTeamConversations(): Promise<TeamConversation[]> {
  const res = await apiFetch<Envelope<{ conversations: TeamConversation[] }>>(
    '/api/team-chat/conversations',
    { headers: authHeaders() },
  );
  return res.data?.conversations ?? [];
}

export async function getUnreadSummary(): Promise<UnreadSummary> {
  const res = await apiFetch<Envelope<UnreadSummary>>('/api/team-chat/unread-summary', {
    headers: authHeaders(),
  });
  return { total: res.data?.total ?? 0, byConversation: res.data?.byConversation ?? {} };
}

/** Get-or-create the DM with another user; returns the conversation either way. */
export async function openDm(userId: string): Promise<TeamConversation> {
  const res = await apiFetch<Envelope<{ conversation: TeamConversation }>>(
    '/api/team-chat/conversations/dm',
    { method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ userId }) },
  );
  return res.data.conversation;
}

/**
 * Persist the caller's own sidebar order. `conversationIds` is the full new
 * order; the server writes the index onto the caller's membership rows only.
 */
export async function reorderConversations(conversationIds: string[]): Promise<void> {
  await apiFetch<Envelope<{ reordered: boolean }>>('/api/team-chat/conversations/reorder', {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({ conversationIds }),
  });
}

/** Admin only: create a group chat with the selected members. */
export async function createGroup(input: {
  name: string;
  memberIds: string[];
}): Promise<TeamConversation> {
  const res = await apiFetch<Envelope<{ conversation: TeamConversation }>>(
    '/api/team-chat/conversations',
    { method: 'POST', headers: jsonHeaders(), body: JSON.stringify(input) },
  );
  return res.data.conversation;
}

/** Admin only: rename / add / remove members of a group. */
export async function updateGroup(
  id: string,
  patch: { name?: string; addMemberIds?: string[]; removeMemberIds?: string[] },
): Promise<TeamConversation> {
  const res = await apiFetch<Envelope<{ conversation: TeamConversation }>>(
    `/api/team-chat/conversations/${encodeURIComponent(id)}`,
    { method: 'PATCH', headers: jsonHeaders(), body: JSON.stringify(patch) },
  );
  return res.data.conversation;
}

/** Admin only: delete a group conversation (and all its messages). */
export async function deleteGroup(id: string): Promise<void> {
  await apiFetch<Envelope<{ deleted: boolean }>>(
    `/api/team-chat/conversations/${encodeURIComponent(id)}`,
    { method: 'DELETE', headers: authHeaders() },
  );
}

export async function listMessages(
  conversationId: string,
  cursor?: string | null,
): Promise<MessagesPage> {
  const qs = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  const res = await apiFetch<Envelope<MessagesPage>>(
    `/api/team-chat/conversations/${encodeURIComponent(conversationId)}/messages${qs}`,
    { headers: authHeaders() },
  );
  return { messages: res.data?.messages ?? [], nextCursor: res.data?.nextCursor ?? null };
}

export async function sendMessage(
  conversationId: string,
  input: { body: string; attachments?: TeamChatAttachment[]; replyToId?: string | null },
): Promise<TeamChatMessage> {
  const attachments = (input.attachments ?? []).map(({ key, fileName, contentType, size }) => ({
    key,
    fileName,
    contentType,
    size,
  }));
  const res = await apiFetch<Envelope<{ message: TeamChatMessage }>>(
    `/api/team-chat/conversations/${encodeURIComponent(conversationId)}/messages`,
    {
      method: 'POST',
      headers: jsonHeaders(),
      body: JSON.stringify({
        body: input.body,
        attachments: attachments.length > 0 ? attachments : undefined,
        replyToId: input.replyToId ?? undefined,
      }),
    },
  );
  return res.data.message;
}

export async function editMessage(id: string, body: string): Promise<TeamChatMessage> {
  const res = await apiFetch<Envelope<{ message: TeamChatMessage }>>(
    `/api/team-chat/messages/${encodeURIComponent(id)}`,
    { method: 'PATCH', headers: jsonHeaders(), body: JSON.stringify({ body }) },
  );
  return res.data.message;
}

export async function deleteMessage(id: string): Promise<void> {
  await apiFetch<Envelope<{ message: TeamChatMessage }>>(
    `/api/team-chat/messages/${encodeURIComponent(id)}`,
    { method: 'DELETE', headers: authHeaders() },
  );
}

export async function pinMessage(id: string): Promise<TeamChatMessage> {
  const res = await apiFetch<Envelope<{ message: TeamChatMessage }>>(
    `/api/team-chat/messages/${encodeURIComponent(id)}/pin`,
    { method: 'POST', headers: jsonHeaders(), body: JSON.stringify({}) },
  );
  return res.data.message;
}

export async function unpinMessage(id: string): Promise<TeamChatMessage> {
  const res = await apiFetch<Envelope<{ message: TeamChatMessage }>>(
    `/api/team-chat/messages/${encodeURIComponent(id)}/pin`,
    { method: 'DELETE', headers: authHeaders() },
  );
  return res.data.message;
}

export async function listPins(conversationId: string): Promise<TeamChatMessage[]> {
  const res = await apiFetch<Envelope<{ messages: TeamChatMessage[] }>>(
    `/api/team-chat/conversations/${encodeURIComponent(conversationId)}/pins`,
    { headers: authHeaders() },
  );
  return res.data?.messages ?? [];
}

/** Toggle the caller's reaction with the given emoji on/off. */
export async function toggleReaction(id: string, emoji: string): Promise<TeamChatMessage> {
  const res = await apiFetch<Envelope<{ message: TeamChatMessage }>>(
    `/api/team-chat/messages/${encodeURIComponent(id)}/reactions`,
    { method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ emoji }) },
  );
  return res.data.message;
}

/** One matching message, enough to render a row and jump to it. */
export interface TeamChatSearchResult {
  messageId: string;
  conversationId: string;
  conversationType: 'DM' | 'GROUP';
  /** Group name, or the DM peer's name — where the message was said. */
  conversationName: string;
  authorName: string;
  snippet: string;
  createdAt: string;
}

/**
 * Message search. Groups, DMs and colleagues are filtered client-side from data
 * the sidebar already holds, so this is the only part of search that needs the
 * server. Pass `conversationId` to scope it to the open conversation.
 */
export async function searchTeamChat(
  q: string,
  conversationId?: string,
): Promise<TeamChatSearchResult[]> {
  const params = new URLSearchParams({ q });
  if (conversationId) params.set('conversationId', conversationId);
  const res = await apiFetch<Envelope<{ results: TeamChatSearchResult[] }>>(
    `/api/team-chat/search?${params.toString()}`,
    { headers: authHeaders() },
  );
  return res.data?.results ?? [];
}

export async function fetchReceipts(conversationId: string): Promise<ChatReadReceipt[]> {
  const res = await apiFetch<Envelope<{ receipts: ChatReadReceipt[] }>>(
    `/api/team-chat/conversations/${encodeURIComponent(conversationId)}/receipts`,
    { headers: authHeaders() },
  );
  return res.data?.receipts ?? [];
}

/**
 * Move the caller's read watermark up to `messageId`. The server stamps that
 * message's own createdAt, so a mark can never claim a message the client
 * hasn't rendered.
 */
export async function markConversationRead(
  conversationId: string,
  messageId: string,
): Promise<void> {
  await apiFetch<Envelope<{ read: boolean }>>(
    `/api/team-chat/conversations/${encodeURIComponent(conversationId)}/read`,
    { method: 'POST', headers: jsonHeaders(), body: JSON.stringify({ messageId }) },
  );
}

// --- Attachment upload (base64 staging, mirrors chatApi.uploadChatAttachment)

export const MAX_TEAM_ATTACHMENT_BYTES = 10 * 1024 * 1024;

const TEAM_ATTACHMENT_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/webp',
  'text/plain',
  'text/markdown',
  'application/json',
  'text/csv',
  'application/zip',
];

export function isSupportedTeamAttachment(file: File): boolean {
  // Files with no browser-detected type upload as octet-stream.
  return !file.type || TEAM_ATTACHMENT_TYPES.includes(file.type.toLowerCase());
}

/** Upload a file to team-chat staging and return its attachment reference. */
export async function uploadTeamChatAttachment(
  file: File,
  onProgress?: UploadProgressHandler,
): Promise<TeamChatAttachment> {
  const dataBase64 = await readFileAsBase64(file, onProgress);
  const res = await apiFetch<Envelope<TeamChatAttachment>>('/api/team-chat/attachments', {
    method: 'POST',
    headers: jsonHeaders(),
    body: JSON.stringify({
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
      dataBase64,
    }),
  });
  return res.data;
}
